import { vars } from '@etg/wings';
import { useTranslation } from '@eti/providers';
import type { BestPricePerDay } from '@eti/schema-types';
import { css, cx, keyframes } from '@eti/styles';
import { isBefore, isSameDay, parseISO } from 'date-fns';
import type { ButtonHTMLAttributes, ReactElement } from 'react';
import { useEffect, useRef } from 'react';
import type { Modifiers } from 'react-day-picker';

const priceCategories: Record<
  string,
  { ariaLabel: string; priceLabel: string | ReactElement; backgroundColor: string; color: string }
> = {
  LOW: {
    ariaLabel: 'ScreenReader.Calendar.Day.Price.Low.AriaLabel',
    priceLabel: '$',
    backgroundColor: vars.colors.calendar.price.low.background,
    color: vars.colors.calendar.price.low.foreground,
  },
  HIGH: {
    ariaLabel: 'ScreenReader.Calendar.Day.Price.High.AriaLabel',
    priceLabel: '$$$',
    backgroundColor: vars.colors.calendar.price.high.background,
    color: vars.colors.calendar.price.high.foreground,
  },
  MEDIUM: {
    ariaLabel: 'ScreenReader.Calendar.Day.Price.Medium.AriaLabel',
    priceLabel: '$$',
    backgroundColor: vars.colors.calendar.price.medium.background,
    color: vars.colors.calendar.price.medium.foreground,
  },
  NOT_AVAILABLE: {
    ariaLabel: 'ScreenReader.Calendar.Day.Price.Medium.AriaLabel',
    priceLabel: '···',
    backgroundColor: vars.colors.calendar.price.unknown.background,
    color: vars.colors.calendar.price.unknown.foreground,
  },
};

const shine = keyframes`
  0% { inset-inline-start : -33px; }
  100% { inset-inline-start: 33px; }
`;

const loadingStyles = css`
  background-color: #e5e5e5;
  height: 16px;
  overflow: hidden;
  position: relative;
  width: 100%;

  &::before {
    animation: linear;
    animation-duration: 1s;
    animation-iteration-count: infinite;
    animation-name: ${shine};
    background-image: linear-gradient(to right, #e5e5e500 0%, #efededcc, #e5e5e500 100%);
    background-repeat: repeat-x;
    content: '';
    height: 100%;
    position: absolute;
    transform: skewX(-45deg);
    width: 20px;
  }
`;

const getPriceStyle = (category?: string) => css`
  ${category ? `background-color: ${priceCategories[category].backgroundColor}` : ''};

  align-items: center;
  border-radius: 2px;
  color: ${category ? priceCategories[category].color : ''};
  display: flex;
  font-size: 0.6rem;
  height: 16px;
  justify-content: center;
  padding: 1px 0;
  text-align: center;
  width: 100%;
`;

const containerStyles = (shouldShowPriceIndicator: boolean) => css`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 4px;
  height: 100%;
  padding: ${shouldShowPriceIndicator ? `2px 6px 6px` : `0 4px 4px`};
  position: relative;
  width: 100%;
`;

const dayNumberStyles = css`
  padding-top: 2px;
  text-align: center;
`;

export interface PriceCalendarProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  date: Date;
  modifiers: Modifiers;
  priceCalendarData?: BestPricePerDay[];
  loading: boolean;
}

const CustomDayWithPriceIndicator = ({
  date,
  priceCalendarData,
  modifiers,
  loading,
  ...buttonProps
}: PriceCalendarProps) => {
  const { t } = useTranslation();
  const ref = useRef<HTMLButtonElement>(null);
  const priceData = priceCalendarData?.find((d) => isSameDay(parseISO(d.date), date));
  const lastDayOfPriceData = priceCalendarData?.[priceCalendarData?.length - 1];
  const shouldShowPriceIndicator =
    !modifiers.disabled &&
    Boolean(lastDayOfPriceData?.date && !isBefore(new Date(lastDayOfPriceData.date), date));
  const { className, ...otherButtonProps } = buttonProps;
  const currentPriceCategory = priceCategories[priceData?.priceCategory ?? 'NOT_AVAILABLE'];

  useEffect(() => {
    if (modifiers.focused) {
      ref.current?.focus();
    }
  }, [modifiers.focused]);

  return (
    <button
      className={cx(className, containerStyles(shouldShowPriceIndicator))}
      ref={ref}
      type="button"
      {...otherButtonProps}
    >
      <div className={dayNumberStyles}>{date.getDate()}</div>
      {shouldShowPriceIndicator ? (
        <div
          aria-label={t(currentPriceCategory.ariaLabel)}
          className={getPriceStyle(priceData?.priceCategory ?? 'NOT_AVAILABLE')}
          data-testid="price-calendar-indicator"
        >
          {currentPriceCategory.priceLabel}
        </div>
      ) : (
        <div
          className={cx(getPriceStyle(), {
            [loadingStyles]: !modifiers.disabled && loading,
          })}
          data-testid="price-calendar-loading-indicator"
        >
          {'   '}
        </div>
      )}
    </button>
  );
};

export default CustomDayWithPriceIndicator;
